import isNull from "lodash/isNull";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { BlueSmiley } from "../../assets/icons";
import useStripePaymentMethodStore, {
  STRIPE_PAYMENT_METHODS_QUERY_KEYS,
} from "./store";
import {
  ActionSheetButton,
  ActionSheetOptions,
  useIonActionSheet,
} from "@ionic/react";
import { HookOverlayOptions } from "@ionic/react/dist/types/hooks/HookOverlayOptions";
import { useFormsStore } from "../../../Common/store/common";
import { useDialogStore } from "../WLDialog/dialogs";
import { removePaymentMethod, setDefaultPayment } from "../../api/stripe";
import { getErrorMsg, useEffectOnlyOnce } from "../../../Common/utils/common";
import { bugNotif } from "../../api/bugsnag";
import { useWebUserStore } from "../../CommonStores/user/users";

const useStripePaymentMethodsActionsProvider = ({
  hasOtherPaymentMethod,
}: {
  hasOtherPaymentMethod?: boolean;
}) => {
  const client = useQueryClient();
  const [present] = useIonActionSheet();

  const { user } = useWebUserStore();
  const { setFormLoading } = useFormsStore();
  const { showDialog } = useDialogStore();
  const {
    reset,
    stripeShowAddCard,
    existingSubscription,
    existingSubscriptionLoading,
    existingSubscriptionLoadFailed,
    setError,
  } = useStripePaymentMethodStore();

  const { mutate: onRemoveCard } = useMutation({
    mutationFn: removePaymentMethod,
    onMutate: () => {
      setFormLoading(true);
    },
    onSuccess: () => {
      setFormLoading(false);
      client.invalidateQueries({
        queryKey: [STRIPE_PAYMENT_METHODS_QUERY_KEYS.paymentMethods],
      });
    },
    onError: (err: any) => {
      setFormLoading(false);
      const error = err as any;
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg ?? err);
      bugNotif("onRemoveCard", rawErrorMsg);
    },
  });

  const confirmRemoveCard = (last4Digits: string, paymentMethodId: string) => {
    if (user === undefined || isNull(user) || user.stripeId === undefined) {
      return setError("No user found. Please re-open app and login.");
    }

    showDialog({
      type: "YesNo",
      component: (
        <div className="wl-warning-cta-dialog">
          <div className="wl-h1 ion-text-center">Remove Payment Method</div>
          <div className="wl-body-4 ion-padding-vertical ion-text-center">
            You're about to remove your payment method ending in{" "}
            <b>(**** {last4Digits})</b>. Do you want to proceed?
          </div>
        </div>
      ),
      dialogIcon: <BlueSmiley />,
      confirmYesLabel: "PROCEED",
      confirmNoLabel: "CANCEL",
      reverseColorOption: true,
      onPressYes: () => {
        onRemoveCard({ paymentMethodId });
      },
    });
  };

  const presetCardOptions = (
    last4Digits: string,
    isDefault: boolean,
    id: string,
    presentFunc: (
      options: ActionSheetOptions & HookOverlayOptions
    ) => Promise<void>
  ) => {
    presentFunc({
      header: `**** ${last4Digits}`,
      buttons: [
        {
          text: "Remove",
          role: "destructive",
          handler: () => {
            confirmRemoveCard(last4Digits, id);
          },
          data: {
            action: "delete",
          },
        },
        ...(!isDefault
          ? ([
              {
                text: "Set as Default",
                handler: () => {
                  confirmSetDefault(last4Digits, id);
                },
              },
            ] as ActionSheetButton<any>[])
          : []),
        {
          text: "Cancel",
          role: "cancel",
          data: {
            action: "cancel",
          },
        },
      ],
    });
  };

  const { mutate: onSetDefaultPayment } = useMutation({
    mutationFn: setDefaultPayment,
    onMutate: () => {
      setFormLoading(true);
    },
    onSuccess: () => {
      setFormLoading(false);
      client.invalidateQueries({
        queryKey: [STRIPE_PAYMENT_METHODS_QUERY_KEYS.paymentMethods],
      });
    },
    onError: (err: any) => {
      setFormLoading(false);
      const error = err as any;
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg ?? err);
      bugNotif("onSetDefaultPayment", rawErrorMsg);
    },
  });

  const confirmSetDefault = (last4Digits: string, paymentMethodId: string) => {
    if (user === undefined || isNull(user) || user.stripeId === undefined) {
      return setError("No user found. Please re-open app and login.");
    }

    // Without a confirmed subscription read we would send an empty subscriptionId,
    // which updates the customer default but leaves the membership on the old card.
    if (existingSubscriptionLoading) {
      return setError(
        "We're still confirming your membership details. Please try again in a moment."
      );
    }

    if (existingSubscriptionLoadFailed) {
      return setError(
        "We couldn't confirm your membership details. Please try again."
      );
    }

    showDialog({
      type: "YesNo",
      component: (
        <div className="wl-warning-cta-dialog">
          <div className="wl-h1 ion-text-center">
            Set Default Payment Method
          </div>
          <div className="wl-body-4 ion-padding-vertical ion-text-center">
            You're about to change your payment method to card ending in{" "}
            <b>(**** {last4Digits})</b>. Do you want to proceed?
          </div>
        </div>
      ),
      dialogIcon: <BlueSmiley />,
      confirmYesLabel: "PROCEED",
      confirmNoLabel: "CANCEL",
      reverseColorOption: true,
      onPressYes: () => {
        onSetDefaultPayment({
          paymentMethodId,
          customerId: user.stripeId!,
          subscriptionId:
            existingSubscription !== undefined && !isNull(existingSubscription)
              ? existingSubscription.id
              : "",
        });
      },
    });
  };

  useEffectOnlyOnce(() => {
    return () => {
      return () => {
        reset({ hasOtherPaymentMethod });
      };
    };
  });

  return {
    stripeShowAddCard,
    presetCardOptions,
  };
};

export default useStripePaymentMethodsActionsProvider;
